import { create } from 'jss';
import jssPreset from '@mui/styles/jssPreset';

import browser, { dom } from '../../browser';

import baseRichContentStyles from '../styles/base';

import codeHighlight from '../modules/code-highlight';
import mathService from '../modules/math';
import handleFormAssemblyLinks from '../modules/form-assembly/services/handleFormAssemblyLinks';
import handleRootMeLinks from '../modules/root-me/services/handleRootmeLinks';
import claireSemantic from '../modules/claire/services/semantic';
import smileys from '../modules/smileys';
import images from '../modules/images';
import videos from '../modules/videos';
import codeSandbox from '../modules/code-sandbox';
import handleRootmeV2Links from '../modules/root-me-v2/services/handleRootmeV2Links';

const parsers = {
  offlineOptions: {},
};

const CLAIRE_TO_ACE_LANGUAGES = {
  c: 'c_cpp',
  'html+php': 'php',
  cpp: 'c_cpp',
  nasm: 'assembly_x86',
  'objective-c': 'objectivec',
};

function getAceLanguageFromClaireLanguage(claireLanguage) {
  return CLAIRE_TO_ACE_LANGUAGES[claireLanguage] || claireLanguage;
}

parsers.parseVideos = function parseVideos(domContainer, shouldLockMediaContent) {
  videos.insert.init(domContainer, shouldLockMediaContent);
};

parsers.parseVideosOffline = function parseVideosOffline(domContainer) {
  const videosToParse = dom.nodeListToArray(domContainer.getElementsByTagName('video'));

  const videoOptions = this.offlineOptions.videos || {};
  const useOnlineFallback = this.offlineOptions.useOnlineFallback || false;

  videosToParse.forEach((video) => {
    const fallbackSrc = videoOptions[video.id];

    if (fallbackSrc) {
      video.setAttribute('src', fallbackSrc);
      video.setAttribute('controls', '1');
      video.setAttribute('class', 'oc-video__iframe');

      const videoWrapper = browser.document.createElement('div');
      videoWrapper.className = 'oc-video';

      video.parentNode.insertBefore(videoWrapper, video);
      videoWrapper.appendChild(video);
    } else if (useOnlineFallback) {
      videos.insert.parseVideo(video);
    }
  });
};

parsers.parseCodes = function parseCodes(domContainer) {
  const preContainer = dom.nodeListToArray(domContainer.querySelectorAll('pre'));
  const codes = dom
    .nodeListToArray(domContainer.querySelectorAll('pre code'))
    .filter((code) => !!claireSemantic.getClaireSemantic(code));

  codes.forEach((code, index) => {
    const claireLanguage = claireSemantic.getClaireSemantic(code);
    const language = getAceLanguageFromClaireLanguage(claireLanguage);

    codeHighlight.ace.syntaxHighlight(code, preContainer[index], language, true);
  });
};

parsers.parseCodeSandbox = function parseCodeSandbox(domContainer, shouldLockMediaContent) {
  codeSandbox.insert.init(domContainer, shouldLockMediaContent);
};

parsers.parseMaths = function parseMaths(domContainer) {
  const maths = dom.nodeListToArray(domContainer.getElementsByTagName('math'));

  if (!maths.length) {
    return;
  }

  mathService.mathjax.highlight(maths).then(() => {
    maths.forEach((math) => {
      const { parentNode } = math;
      const span = browser.document.createElement('span');
      span.innerHTML = math.innerHTML;
      parentNode.insertBefore(span, math);
      parentNode.removeChild(math);
    });
  });
};

parsers.parseRootMeLinks = handleRootMeLinks;
parsers.parseFormAssemblyLinks = handleFormAssemblyLinks;
parsers.parseRootMeV2Links = handleRootmeV2Links;

parsers.parseMathsOffline = function parseMathsOffline(domContainer) {
  const maths = dom.nodeListToArray(domContainer.getElementsByTagName('math'));

  maths.forEach((math) => {
    const { parentNode } = math;
    const mathReplacement = browser.document.createElement('em');

    mathReplacement.innerHTML = math.innerHTML;
    parentNode.insertBefore(mathReplacement, math);
    parentNode.removeChild(math);
  });
};

parsers.parseEmojis = function parseEmojis(domContainer) {
  smileys.oc.parse(domContainer);
};

parsers.parseTables = function parseTables(domContainer) {
  dom.nodeListToArray(domContainer.querySelectorAll('table')).forEach((table) => {
    const wrapper = browser.document.createElement('div');
    wrapper.classList.add('oc-tableContainer');

    table.parentNode.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });
};

parsers.parseImages = function parseImages(domContainer) {
  images.zoom.init(domContainer);
};

parsers.parseImagesOffline = function parseImages(domContainer) {
  const pictures = dom.nodeListToArray(domContainer.querySelectorAll('img'));

  const imageOptions = this.offlineOptions.images || {};

  pictures.forEach((picture) => {
    const fallbackSrc = imageOptions[picture.id];

    if (fallbackSrc) {
      picture.setAttribute('src', fallbackSrc);
    }
  });

  this.parseImages(domContainer);
};

parsers.useStyles = function useStyle(domContainer, styles) {
  if (domContainer.classList.contains('oc-richContent')) {
    return;
  }

  const jss = create(jssPreset());
  const sheet = jss.createStyleSheet(styles);
  sheet.attach();

  domContainer.classList.add('oc-richContent');
  domContainer.classList.add(sheet.classes.root);
};

parsers.parse = function parse(domContainer, styles = baseRichContentStyles, options = {}) {
  const { shouldLockMediaContent } = options;
  const AVAILABLE_PARSERS = {
    VIDEOS: 'parseVideos',
    CODE: 'parseCodes',
    MATHS: 'parseMaths',
    IMAGES: 'parseImages',
    EMOJIS: 'parseEmojis',
    TABLES: 'parseTables',
    SANDBOX: 'parseCodeSandbox',
    ROOTME: 'parseRootMeLinks',
    FORM_ASSEMBLY: 'parseFormAssemblyLinks',
    ROOTME_V2: 'parseRootMeV2Links',
  };

  parsers.useStyles(domContainer, styles);

  Object.values(AVAILABLE_PARSERS).forEach((method) => {
    try {
      if (method === AVAILABLE_PARSERS.VIDEOS || method === AVAILABLE_PARSERS.SANDBOX) {
        parsers[method].call(this, domContainer, shouldLockMediaContent);
      } else {
        parsers[method].call(this, domContainer);
      }
      // eslint-disable-next-line no-empty
    } catch (e) {}
  });
};

parsers.parseOffline = function parseOffline(
  domContainer,
  offlineOptions = {},
  styles = baseRichContentStyles
) {
  this.offlineOptions = offlineOptions;
  parsers.useStyles(domContainer, styles);

  [
    'parseVideosOffline',
    'parseCodes',
    'parseMathsOffline',
    'parseImagesOffline',
    'parseEmojis',
    'parseTables',
  ].forEach((method) => parsers[method].call(this, domContainer));
};

parsers.clearImagesZoom = images.zoom.clear;

export default parsers;
